import React, { useContext, useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
} from "@mui/material";
import ExpandLessRounded from "@mui/icons-material/ExpandLessRounded";
import ExpandMoreRounded from "@mui/icons-material/ExpandMoreRounded";
import { DrawerContext } from "context/DrawerContext";

// Collapsible sidebar group (accordion). Auto-expands when the current route
// falls under one of `matchPaths`, while still allowing manual toggling.
// In the mini (icon-only) drawer there is no room for an accordion, so the
// header is dropped and children render as flat, un-indented icon items.
const NavListGroup = ({ icon, text, secondary, matchPaths = [], children }) => {
  const open = useContext(DrawerContext);
  const { pathname } = useLocation();

  const onChildRoute = matchPaths.some(
    (p) => pathname === p || pathname.startsWith(`${p}/`)
  );
  const [expanded, setExpanded] = useState(onChildRoute);

  useEffect(() => {
    if (onChildRoute) setExpanded(true);
  }, [onChildRoute]);

  // Tell child NavListItems to indent their content (only meaningful when the
  // drawer is expanded — the mini rail keeps them centered).
  const items = React.Children.map(children, (child) =>
    React.isValidElement(child)
      ? React.cloneElement(child, { nested: open })
      : child
  );

  if (!open) return <>{items}</>;

  return (
    <>
      <ListItem disablePadding sx={{ display: "block" }}>
        <ListItemButton
          onClick={() => setExpanded((prev) => !prev)}
          selected={onChildRoute && !expanded}
          sx={{ minHeight: 48, px: 2.5 }}
        >
          <ListItemIcon sx={{ minWidth: 0, mr: 3, justifyContent: "center" }}>
            {icon}
          </ListItemIcon>
          <ListItemText
            primary={text}
            secondary={secondary}
            primaryTypographyProps={{ noWrap: true }}
            secondaryTypographyProps={{ noWrap: true }}
            sx={{
              minWidth: 0,
              m: 0,
              "& .MuiListItemText-primary": { color: "text.primary" },
              "& .MuiListItemText-secondary": { color: "text.secondary" },
            }}
          />
          {expanded ? (
            <ExpandLessRounded sx={{ flexShrink: 0, ml: 1, color: "text.secondary" }} />
          ) : (
            <ExpandMoreRounded sx={{ flexShrink: 0, ml: 1, color: "text.secondary" }} />
          )}
        </ListItemButton>
      </ListItem>
      <Collapse in={expanded} timeout="auto" unmountOnExit>
        <List disablePadding>{items}</List>
      </Collapse>
    </>
  );
};

export default NavListGroup;
